import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { TrashIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/TrashIcon'
import { SecondaryIconButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryIconButton/SecondaryIconButton'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { sumDuration } from '../data/npTestPlanMeasureUtils'
import { NpTestPlanMeasure } from '../data/npTestPlanTypes'
import { useNpTestPlanMeasureLabels } from '../hooks/useNpTestPlanMeasureLabels'

export interface NpTestPlanMeasureRowProps {
  measure: NpTestPlanMeasure
  onRemove?: () => void
  isRemoveDisabled?: boolean
}

const Row = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['16px'],
  minHeight: '72px',
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['16px'],
  borderTopWidth: 1,
  borderTopColor: colors.borderDefault,
  borderStyle: 'solid',
}))

const Name = styled.View({
  flex: 1,
})

const AgeRange = styled.View({
  width: '90px',
})

const Duration = styled.View({
  width: '90px',
})

export const NpTestPlanMeasureRow: FunctionComponent<NpTestPlanMeasureRowProps> = ({
  measure,
  onRemove,
  isRemoveDisabled,
}) => {
  const { formatMessage } = useIntl()
  const { formatAgeRange, formatDuration } = useNpTestPlanMeasureLabels()
  const { colors } = useTheme() as ThemeType

  return (
    <Row testID={tID(`NpTestPlanMeasureRow-${measure.id}`)}>
      <Name>
        <BodyText size={BodyTextSize.DEFAULT} text={measure.name} />
      </Name>
      <AgeRange>
        <BodyText size={BodyTextSize.SMALL} color={colors.textSecondary} text={formatAgeRange(measure)} />
      </AgeRange>
      <Duration>
        <BodyText
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
          text={formatDuration(sumDuration([measure]))}
        />
      </Duration>
      {onRemove && (
        <SecondaryIconButton
          testID={tID(`NpTestPlanMeasureRemove-${measure.id}`)}
          onPress={onRemove}
          disabled={isRemoveDisabled}
          leftIcon={<TrashIcon size={20} />}
          accessibilityLabel={formatMessage(
            {
              defaultMessage: 'Remove {measureName} from the test plan',
              description: 'Announced for the button that takes a test measure off a neuropsych test plan',
            },
            { measureName: measure.name },
          )}
        />
      )}
    </Row>
  )
}
